import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../../../assets/css/delivery-vehicle.css";
import { checkDeliveryVehicle, checkRentalDeliveryVehicle } from "../../../data/loca/deliveryVehicle";

// Alerte « véhicule de livraison indisponible » du formulaire et de la fiche location : livraisons/reprises qui
// tombent pendant une indisponibilité du camion, ou livraisons déjà prévues pendant que le camion lui-même est loué.
// Simple avertissement : la location reste enregistrable (autre véhicule, sous-traitance…).

const toDate = (value) => (value instanceof Date ? value : new Date(value));

// « 19h », « 8h30 ».
export const formatShortTime = (value) => {
    const date = toDate(value);
    return `${date.getHours()}h${date.getMinutes() ? String(date.getMinutes()).padStart(2, "0") : ""}`;
};

const formatDayLabel = (date) => date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });

// « dim. 4 oct. à 19h »
export const formatVehicleDateTime = (value) => `${formatDayLabel(toDate(value))} à ${formatShortTime(value)}`;

// Période lisible : « sam. 4 oct. 8h → 18h », ou « 4 oct. → 6 oct. » pour des journées entières.
export function formatVehiclePeriod(startValue, endValue) {
    const start = toDate(startValue);
    const end = toDate(endValue);
    const isMidnight = (date) => date.getHours() === 0 && date.getMinutes() === 0;

    if (isMidnight(start) && isMidnight(end)) {
        const lastDay = new Date(end.getTime() - 60 * 1000);
        return lastDay.toDateString() === start.toDateString()
            ? `${formatDayLabel(start)} (journée)`
            : `${formatDayLabel(start)} → ${formatDayLabel(lastDay)}`;
    }
    return start.toDateString() === end.toDateString()
        ? `${formatDayLabel(start)} ${formatShortTime(start)} → ${formatShortTime(end)}`
        : `${formatDayLabel(start)} ${formatShortTime(start)} → ${formatDayLabel(end)} ${formatShortTime(end)}`;
}

export const legLabel = (leg) => leg.kind === "delivery"
    ? (leg.withSetup ? "Livraison et installation" : "Livraison")
    : (leg.withSetup ? "Reprise et désinstallation" : "Reprise");

function BlockLine({ block, currentRentalPublicId }) {
    const label = block.source === "rental" && block.rentalPublicId && block.rentalPublicId !== currentRentalPublicId
        ? <Link to={`/${block.rentalPublicId}`}>{block.label}</Link>
        : block.label;
    return (
        <span className="dv-notice__block">
            <b>{label}</b>
            {" · "}
            {formatVehiclePeriod(block.rentalStart ?? block.start, block.rentalEnd ?? block.end)}
            {block.note ? <em> — {block.note}</em> : null}
        </span>
    );
}

export function DeliveryVehicleNotice({ check, currentRentalPublicId, compact = false }) {
    if (!check) return null;
    const legConflicts = check.legConflicts ?? [];
    const planned = check.plannedDeliveries ?? [];
    if (legConflicts.length === 0 && planned.length === 0) return null;

    const vehicleName = check.vehicle?.name || "Le véhicule de livraison";

    return (
        <div className={`dv-notice${compact ? " dv-notice--compact" : ""}`} role="alert" data-cy="deliveryVehicleNotice">
            <i className="pi pi-truck dv-notice__icon" aria-hidden="true"></i>
            <div className="dv-notice__body">
                {legConflicts.length > 0 && (
                    <>
                        <strong className="dv-notice__title">{vehicleName} n'est pas disponible</strong>
                        <ul>
                            {legConflicts.map((leg) => (
                                <li key={`${leg.kind}-${leg.anchor}`}>
                                    <span className="dv-notice__leg">{legLabel(leg)} du {formatVehicleDateTime(leg.anchor)}</span>
                                    {leg.conflicts.map((block, index) => (
                                        <BlockLine key={index} block={block} currentRentalPublicId={currentRentalPublicId} />
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {planned.length > 0 && (
                    <>
                        <strong className="dv-notice__title">
                            {vehicleName} est prévu pour {planned.length} {planned.length > 1 ? "passages" : "passage"} pendant cette location
                        </strong>
                        <ul>
                            {planned.map((leg) => (
                                <li key={`${leg.rentalPublicId}-${leg.kind}`}>
                                    <span className="dv-notice__leg">{legLabel(leg)} du {formatVehicleDateTime(leg.anchor)}</span>
                                    <span className="dv-notice__block">
                                        <Link to={`/${leg.rentalPublicId}`}>{leg.clientName}</Link>
                                        {leg.materialNames?.length ? ` · ${leg.materialNames.join(", ")}` : ""}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {!compact && (
                    <p className="dv-notice__hint">
                        La location reste enregistrable si vous prévoyez un autre véhicule.{" "}
                        <Link to="/outils/vehicule-livraison">Planning du véhicule</Link>
                    </p>
                )}
                {compact && <Link className="dv-notice__link" to="/outils/vehicule-livraison">Planning du véhicule</Link>}
            </div>
        </div>
    );
}

// Contrôle en direct pendant la saisie (debounce) ; null tant que la période ou le mode ne sont pas renseignés.
export function useDeliveryVehicleCheck({ start, end, delivery, materials, tripSeconds, rentalPublicId }) {
    const [check, setCheck] = useState(null);
    const startKey = start ? start.getTime() : null;
    const endKey = end ? end.getTime() : null;
    const materialsKey = (materials ?? []).join(",");
    const needsCheck = Boolean(startKey && endKey && endKey >= startKey && ((delivery && delivery !== "NoDelivery") || materialsKey));

    useEffect(() => {
        if (!needsCheck) {
            setCheck(null);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const result = await checkDeliveryVehicle({
                    start: new Date(startKey),
                    end: new Date(endKey),
                    delivery,
                    materials: materialsKey ? materialsKey.split(",") : [],
                    tripSeconds: Number(tripSeconds) || 0,
                    rentalPublicId
                });
                if (!cancelled) setCheck(result);
            } catch {
                // Contrôle informatif : en cas d'erreur, on n'affiche rien plutôt que de bloquer la saisie.
                if (!cancelled) setCheck(null);
            }
        }, 450);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [needsCheck, startKey, endKey, delivery, materialsKey, tripSeconds, rentalPublicId]);

    return check;
}

// Fiche location : contrôle de la location enregistrée.
export function RentalDeliveryVehicleNotice({ rentalPublicId, refreshKey }) {
    const [check, setCheck] = useState(null);

    useEffect(() => {
        let cancelled = false;
        checkRentalDeliveryVehicle(rentalPublicId)
            .then((result) => { if (!cancelled) setCheck(result); })
            .catch(() => { if (!cancelled) setCheck(null); });
        return () => { cancelled = true; };
    }, [rentalPublicId, refreshKey]);

    return <DeliveryVehicleNotice check={check} currentRentalPublicId={rentalPublicId} compact />;
}
